import React, { useEffect, useState } from 'react';
import { Trash2, Edit, UserCheck, UserX, MoreVertical } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { User, Role } from '../../types';
import { getUsers, deleteUser, updateUser, getRoles } from '../../utils/auth/authService';
import { useNavigate } from 'react-router-dom';

const UsersPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!openDropdownId) return;
    const handleClickOutside = () => setOpenDropdownId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdownId]);

  const loadData = async () => {
    try {
      const [usersData, rolesData] = await Promise.all([
        getUsers(),
        getRoles()
      ]);
      setUsers(usersData);
      setRoles(rolesData);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await updateUser(user.user_id, { is_active: !user.is_active });
      await loadData();
    } catch (err) {
      alert('Failed to update user');
    }
  };

  const handleDelete = async (userId: string, username: string) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) return;
    try {
      await deleteUser(userId);
      await loadData();
    } catch (err) {
      alert('Failed to delete user');
    }
  };

  const getRoleNames = (roleIds: string[]) => {
    return roleIds
      .map(id => roles.find(r => r.role_id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  if (loading) {
    return <div style={{ color: theme.text }}>Loading...</div>;
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ color: theme.text, fontSize: 24, margin: 0 }}>Users</h1>
      </div>

      {users.length === 0 ? (
        <div style={{
          background: theme.cardBackground,
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          padding: 48,
          textAlign: 'center',
        }}>
          <p style={{ color: theme.textSecondary, fontSize: 16, margin: 0 }}>
            No users found
          </p>
        </div>
      ) : (
        <div style={{
          background: theme.cardBackground,
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: theme.background }}>
                <th style={{ padding: 16, textAlign: 'left', color: theme.text, fontWeight: 600, borderBottom: `1px solid ${theme.border}` }}>
                  Username
                </th>
                <th style={{ padding: 16, textAlign: 'left', color: theme.text, fontWeight: 600, borderBottom: `1px solid ${theme.border}` }}>
                  Email
                </th>
                <th style={{ padding: 16, textAlign: 'left', color: theme.text, fontWeight: 600, borderBottom: `1px solid ${theme.border}` }}>
                  Name
                </th>
                <th style={{ padding: 16, textAlign: 'left', color: theme.text, fontWeight: 600, borderBottom: `1px solid ${theme.border}` }}>
                  Roles
                </th>
                <th style={{ padding: 16, textAlign: 'left', color: theme.text, fontWeight: 600, borderBottom: `1px solid ${theme.border}` }}>
                  Status
                </th>
                <th style={{ padding: 16, textAlign: 'left', color: theme.text, fontWeight: 600, borderBottom: `1px solid ${theme.border}` }}>
                  Auth Type
                </th>
                <th style={{ padding: 16, textAlign: 'center', color: theme.text, fontWeight: 600, borderBottom: `1px solid ${theme.border}` }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.user_id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <td style={{ padding: 16, color: theme.text }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {user.username}
                      {user.is_superuser && (
                        <span style={{
                          padding: '2px 6px',
                          background: '#fef3c7',
                          color: '#92400e',
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                        }}>
                          SUPERUSER
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: 16, color: theme.text }}>
                    {user.email}
                  </td>
                  <td style={{ padding: 16, color: theme.text }}>
                    {user.first_name && user.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : '-'}
                  </td>
                  <td style={{ padding: 16, color: theme.textSecondary, fontSize: 14 }}>
                    {getRoleNames(user.roles) || '-'}
                  </td>
                  <td style={{ padding: 16 }}>
                    {user.is_active ? (
                      <span style={{
                        padding: '4px 8px',
                        background: '#d1fae5',
                        color: '#065f46',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                      }}>
                        Active
                      </span>
                    ) : (
                      <span style={{
                        padding: '4px 8px',
                        background: '#fee2e2',
                        color: '#991b1b',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                      }}>
                        Inactive
                      </span>
                    )}
                  </td>
                  <td style={{ padding: 16, color: theme.textSecondary, fontSize: 14 }}>
                    {user.sso_provider_id ? 'SSO' : 'Local'}
                  </td>
                  <td style={{ padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdownId(openDropdownId === user.user_id ? null : user.user_id);
                        }}
                        style={{
                          padding: 8,
                          background: 'transparent',
                          border: `1px solid ${theme.border}`,
                          borderRadius: 4,
                          cursor: 'pointer',
                          color: theme.text,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title="More actions"
                      >
                        <MoreVertical size={16} />
                      </button>
                      {openDropdownId === user.user_id && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: '4px',
                            background: theme.cardBackground,
                            border: `1px solid ${theme.border}`,
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            minWidth: '160px',
                            zIndex: 10,
                            overflow: 'hidden'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId(null);
                              navigate(`/auth/users/${user.user_id}/edit`);
                            }}
                            style={{
                              width: '100%',
                              background: 'transparent',
                              color: theme.text,
                              border: 'none',
                              padding: '10px 16px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              fontSize: '14px',
                              textAlign: 'left',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = theme.hoverBackground}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <Edit size={16} />
                            Edit Roles
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId(null);
                              handleToggleActive(user);
                            }}
                            style={{
                              width: '100%',
                              background: 'transparent',
                              color: theme.text,
                              border: 'none',
                              padding: '10px 16px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              fontSize: '14px',
                              textAlign: 'left',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = theme.hoverBackground}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            {user.is_active ? <UserX size={16} /> : <UserCheck size={16} />}
                            {user.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId(null);
                              handleDelete(user.user_id, user.username);
                            }}
                            style={{
                              width: '100%',
                              background: 'transparent',
                              color: theme.error,
                              border: 'none',
                              borderTop: `1px solid ${theme.border}`,
                              padding: '10px 16px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              fontSize: '14px',
                              textAlign: 'left',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = theme.errorLight}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <Trash2 size={16} />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
